import React, { useState } from "react";
import { api } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { Compass, Loader2, MapPin, Sparkles, Wine, UtensilsCrossed, Bed, Clock, Route as RouteIcon, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { canUse, consume, getRemaining, isGuest } from "@/lib/guest";
import GuestBanner from "@/components/GuestBanner";

const SUGGESTED_REGIONS = ["Bourgogne", "Bordeaux", "Champagne", "Loire", "Rhône Nord", "Alsace", "Saint-Émilion", "Provence", "Languedoc", "Beaujolais"];
const INTEREST_TAGS = [
  { key: "gastronomie", label_fr: "Gastronomie", label_en: "Gastronomy" },
  { key: "velo", label_fr: "Vélo", label_en: "Cycling" },
  { key: "culture", label_fr: "Culture & patrimoine", label_en: "Culture & heritage" },
  { key: "nature", label_fr: "Nature & balades", label_en: "Nature & walks" },
  { key: "luxe", label_fr: "Luxe & spa", label_en: "Luxury & spa" },
  { key: "famille", label_fr: "Famille", label_en: "Family-friendly" },
];

const stopIcon = (type) => {
  const t = (type || "").toLowerCase();
  if (t.includes("dom")) return <Wine className="w-4 h-4 text-[#E5276A]" />;
  if (t.includes("rest")) return <UtensilsCrossed className="w-4 h-4 text-[#E5276A]" />;
  if (t.includes("hôt") || t.includes("hot")) return <Bed className="w-4 h-4 text-[#E5276A]" />;
  return <MapPin className="w-4 h-4 text-[#E5276A]" />;
};

export default function Itineraries() {
  const { lang } = useT();
  const [region, setRegion] = useState("");
  const [base, setBase] = useState("");
  const [days, setDays] = useState(2);
  const [interests, setInterests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [remaining, setRemaining] = useState(getRemaining("itinerary"));

  const labelFor = (tag) => (lang === "fr" ? tag.label_fr : tag.label_en);

  const toggle = (k) => setInterests((arr) => (arr.includes(k) ? arr.filter((x) => x !== k) : [...arr, k]));

  const submit = async (e) => {
    e?.preventDefault();
    if (!region.trim()) {
      toast.error(lang === "fr" ? "Renseignez une région" : "Please enter a region");
      return;
    }
    if (!canUse("itinerary")) {
      setRemaining(0);
      toast.error(lang === "fr" ? "Quota gratuit atteint. Inscrivez-vous pour usage illimité." : "Free quota reached. Sign up for unlimited use.");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/itineraries/generate", {
        region: region.trim(),
        days,
        interests,
        base_city: base.trim() || null,
      });
      setResult(data);
      const c = consume("itinerary");
      setRemaining(c.remaining === Infinity ? null : c.remaining);
    } catch (err) {
      toast.error(lang === "fr" ? "Génération impossible. Réessayez." : "Generation failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-5 md:px-10 py-12 md:py-16">
      <div className="max-w-2xl">
        <span className="chip-pink">{lang === "fr" ? "Œnotourisme" : "Wine tourism"}</span>
        <h1 className="font-display text-4xl md:text-5xl text-[#1F3A5F] mt-4 leading-[1.05]">
          {lang === "fr" ? "Itinéraires sur mesure" : "Tailored itineraries"}
        </h1>
        <p className="text-[#4A5568] mt-3">
          {lang === "fr"
            ? "geoVINUM AI trace votre route : domaines, restaurants, balades — avec liens de réservation Winalist, Booking, OpenTable."
            : "geoVINUM AI plans your trip: wineries, restaurants, walks — with Winalist, Booking & OpenTable booking links."}
        </p>
      </div>

      <form onSubmit={submit} className="mt-8 rounded-2xl border border-[#1F3A5F]/12 bg-white p-6 md:p-7 space-y-5" data-testid="itinerary-form">
        {isGuest() && <GuestBanner feature="itinerary" remaining={remaining} />}
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#8B95A1]">
              {lang === "fr" ? "Région *" : "Region *"}
            </label>
            <input
              data-testid="itinerary-region"
              value={region}
              onChange={(e) => setRegion(e.target.value)}
              placeholder="Bourgogne, Bordeaux, Loire…"
              className="mt-1 w-full px-3 py-2.5 rounded-lg border border-[#1F3A5F]/15 text-[#1F3A5F] focus:outline-none focus:border-[#E5276A]"
            />
            <div className="mt-2 flex flex-wrap gap-1.5">
              {SUGGESTED_REGIONS.map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setRegion(r)}
                  data-testid={`itinerary-suggest-${r}`}
                  className="px-2.5 py-1 rounded-full text-[11px] font-medium bg-[#FBF7F3] text-[#1F3A5F] border border-[#1F3A5F]/10 hover:border-[#E5276A] hover:text-[#E5276A]"
                >
                  {r}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#8B95A1]">
              {lang === "fr" ? "Ville de base (optionnel)" : "Base city (optional)"}
            </label>
            <input
              data-testid="itinerary-base"
              value={base}
              onChange={(e) => setBase(e.target.value)}
              placeholder="Beaune, Saint-Émilion…"
              className="mt-1 w-full px-3 py-2.5 rounded-lg border border-[#1F3A5F]/15 text-[#1F3A5F] focus:outline-none focus:border-[#E5276A]"
            />
            <label className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#8B95A1] mt-4 block">
              {lang === "fr" ? `Durée : ${days} jour${days > 1 ? "s" : ""}` : `Duration: ${days} day${days > 1 ? "s" : ""}`}
            </label>
            <input
              data-testid="itinerary-days"
              type="range"
              min={1}
              max={5}
              value={days}
              onChange={(e) => setDays(parseInt(e.target.value))}
              className="mt-2 w-full accent-[#E5276A]"
            />
          </div>
        </div>

        <div>
          <label className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#8B95A1]">
            {lang === "fr" ? "Centres d'intérêt" : "Interests"}
          </label>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {INTEREST_TAGS.map((tag) => (
              <button
                key={tag.key}
                type="button"
                onClick={() => toggle(tag.key)}
                data-testid={`itinerary-tag-${tag.key}`}
                className={`px-3 py-1.5 rounded-full text-[12px] font-bold border transition-colors ${
                  interests.includes(tag.key)
                    ? "bg-[#E5276A] text-white border-[#E5276A]"
                    : "bg-white text-[#1F3A5F] border-[#1F3A5F]/15 hover:border-[#E5276A]"
                }`}
              >
                {labelFor(tag)}
              </button>
            ))}
          </div>
        </div>

        <button
          type="submit"
          data-testid="itinerary-submit"
          disabled={loading}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#E5276A] text-white text-sm font-bold hover:bg-[#C01661] disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          {loading
            ? lang === "fr"
              ? "geoVINUM AI trace votre itinéraire…"
              : "geoVINUM AI is planning…"
            : lang === "fr"
            ? "Générer l'itinéraire"
            : "Generate itinerary"}
        </button>
      </form>

      {result && (
        <div className="mt-10 space-y-6" data-testid="itinerary-result">
          <div className="rounded-2xl border border-[#1F3A5F]/12 bg-[#FBF7F3] p-6 md:p-8">
            <div className="flex items-start gap-3 flex-wrap">
              <Compass className="w-6 h-6 text-[#E5276A] mt-1" />
              <div className="flex-1 min-w-[200px]">
                <h2 className="font-display text-2xl md:text-3xl text-[#1F3A5F]" data-testid="itinerary-title">
                  {result.title || `${region} — ${days} jour${days > 1 ? "s" : ""}`}
                </h2>
                {result.total_distance_km_approx && (
                  <p className="text-[#4A5568] mt-1 text-sm inline-flex items-center gap-1.5">
                    <RouteIcon className="w-3.5 h-3.5" /> ~{result.total_distance_km_approx} km
                  </p>
                )}
              </div>
              {result.partners && (
                <div className="flex flex-wrap gap-2">
                  <a href={result.partners.winalist} target="_blank" rel="noreferrer" data-testid="itinerary-partner-winalist"
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[12px] font-bold bg-white border border-[#1F3A5F]/15 hover:border-[#E5276A] text-[#1F3A5F]">
                    Winalist <ExternalLink className="w-3 h-3" />
                  </a>
                  <a href={result.partners.booking} target="_blank" rel="noreferrer" data-testid="itinerary-partner-booking"
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[12px] font-bold bg-white border border-[#1F3A5F]/15 hover:border-[#E5276A] text-[#1F3A5F]">
                    Booking.com <ExternalLink className="w-3 h-3" />
                  </a>
                  <a href={result.partners.opentable} target="_blank" rel="noreferrer" data-testid="itinerary-partner-opentable"
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[12px] font-bold bg-white border border-[#1F3A5F]/15 hover:border-[#E5276A] text-[#1F3A5F]">
                    OpenTable <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              )}
            </div>
          </div>

          {(result.days || []).map((d, idx) => (
            <div key={idx} className="rounded-2xl border border-[#1F3A5F]/12 bg-white p-6 md:p-7" data-testid={`itinerary-day-${d.day || idx + 1}`}>
              <div className="flex items-baseline gap-3 flex-wrap">
                <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-[#1F3A5F] text-white text-sm font-bold">
                  {d.day || idx + 1}
                </span>
                <h3 className="font-display text-xl md:text-2xl text-[#1F3A5F]">{d.theme || `Jour ${idx + 1}`}</h3>
              </div>

              <div className="mt-5 space-y-4">
                {(d.stops || []).map((s, i) => (
                  <div key={i} className="flex gap-3 items-start" data-testid={`itinerary-stop-${idx}-${i}`}>
                    <div className="shrink-0 mt-1 w-9 h-9 rounded-full bg-[#FCE4ED] flex items-center justify-center">
                      {stopIcon(s.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-2 flex-wrap">
                        {s.time && <span className="text-[11px] font-bold uppercase tracking-wider text-[#E5276A]">{s.time}</span>}
                        <span className="font-bold text-[#1F3A5F]">{s.name}</span>
                        {s.city && <span className="text-xs text-[#8B95A1]">· {s.city}</span>}
                        {s.duration_min && (
                          <span className="text-[11px] text-[#8B95A1] inline-flex items-center gap-0.5">
                            <Clock className="w-3 h-3" />
                            {s.duration_min}′
                          </span>
                        )}
                      </div>
                      {s.description && <p className="text-sm text-[#4A5568] mt-1 leading-relaxed">{s.description}</p>}
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {s.winalist_url && (
                          <a href={s.winalist_url} target="_blank" rel="noreferrer"
                            className="text-[11px] font-bold inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#FBF7F3] text-[#1F3A5F] border border-[#1F3A5F]/12 hover:border-[#E5276A]">
                            {lang === "fr" ? "Réserver visite" : "Book visit"} <ExternalLink className="w-2.5 h-2.5" />
                          </a>
                        )}
                        {s.opentable_url && (
                          <a href={s.opentable_url} target="_blank" rel="noreferrer"
                            className="text-[11px] font-bold inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#FBF7F3] text-[#1F3A5F] border border-[#1F3A5F]/12 hover:border-[#E5276A]">
                            {lang === "fr" ? "Réserver table" : "Book table"} <ExternalLink className="w-2.5 h-2.5" />
                          </a>
                        )}
                        {s.booking_url && (
                          <a href={s.booking_url} target="_blank" rel="noreferrer"
                            className="text-[11px] font-bold inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#FBF7F3] text-[#1F3A5F] border border-[#1F3A5F]/12 hover:border-[#E5276A]">
                            {lang === "fr" ? "Réserver hôtel" : "Book hotel"} <ExternalLink className="w-2.5 h-2.5" />
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {d.dinner?.name && (
                <div className="mt-6 rounded-xl bg-[#FBF7F3] p-4 flex items-start gap-3">
                  <UtensilsCrossed className="w-4 h-4 text-[#E5276A] mt-1" />
                  <div>
                    <div className="text-[11px] uppercase tracking-wider font-bold text-[#E5276A]">{lang === "fr" ? "Dîner suggéré" : "Suggested dinner"}</div>
                    <div className="font-bold text-[#1F3A5F]">{d.dinner.name}{d.dinner.city ? ` · ${d.dinner.city}` : ""}</div>
                    {d.dinner.description && <p className="text-sm text-[#4A5568] mt-0.5">{d.dinner.description}</p>}
                  </div>
                </div>
              )}
            </div>
          ))}

          {(result.tips || []).length > 0 && (
            <div className="rounded-2xl border border-[#1F3A5F]/12 bg-white p-6">
              <h3 className="font-display text-lg text-[#1F3A5F]">{lang === "fr" ? "Conseils de geoVINUM AI" : "geoVINUM AI's tips"}</h3>
              <ul className="mt-3 space-y-1.5 text-sm text-[#4A5568]">
                {result.tips.map((tip, i) => (
                  <li key={i} className="flex gap-2"><Sparkles className="w-3.5 h-3.5 text-[#E5276A] shrink-0 mt-0.5" /><span>{tip}</span></li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
